import { useQuery } from '@tanstack/react-query';
import { API_KEYS, API_SERVICE } from '@/components/shared/requisitions/api';
import { FILTER_OPTIONS_STALE_MS } from '@/constants/app-constants';
import type { Requisition } from '@/types/requisitions';

interface UseRequisitionGroupSiblingsParams {
  /** `requisition_group_id` of the row. Falsy for an ungrouped requisition. */
  groupId?: number | string | null;
  /**
   * Held false until the surface that needs the siblings is actually open.
   * The group tag itself renders off `group_size`, so nothing on first paint
   * depends on this resolving.
   */
  enabled?: boolean;
}

/**
 * Siblings of one requisition group, fetched on demand.
 *
 * The list page used to pull `requisitions?limit=200` on mount and filter it
 * per row to answer this — the slowest call on the page (p95 10.3s) for data
 * only ever read inside a tooltip or an open menu. `/requisitions/group` answers
 * it directly, and the query key matches the one the `?view_group=` deep link
 * already uses, so rows in the same group share a single request.
 */
export const useRequisitionGroupSiblings = ({
  groupId,
  enabled = true,
}: UseRequisitionGroupSiblingsParams) => {
  // Keyed on a string so a row's numeric group id and the deep link's URL
  // string hit the same cache entry.
  const key = groupId ? String(groupId) : '';
  const { data, isFetching } = useQuery({
    queryKey: [API_KEYS.getRequisitionGroup, key],
    queryFn: () => API_SERVICE.getRequisitionGroup(key),
    enabled: Boolean(key) && enabled,
    staleTime: FILTER_OPTIONS_STALE_MS,
  });

  return {
    siblings: (data?.requisitions ?? []) as Requisition[],
    isLoading: isFetching,
  };
};
